import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, ChevronDown, ChevronRight, Home, Building2, Bath, Armchair, Sparkles, MapPin } from 'lucide-react';
import { LeadCaptureForm } from '../components/forms/LeadCaptureForm';
import { LocatorFeeder } from '../components/LocatorFeeder';
import { PageSEO } from '../components/seo/SchemaMarkup';
import HUB_SCHEMA from '../data/hydrotherapySchema.json';

const INK = '#0A0A0A';
const LIME = '#C7FA3E';
const BLUE = '#0A2E5C';
const LINE = '#E7E7E4';
const MUT = '#5C5C5C';
const SOFT = '#F5F6F1';
const ANTON = { fontFamily: "'Anton', sans-serif", fontWeight: 400, textTransform: 'uppercase' };
const ARCHIVO = { fontFamily: "'Archivo', 'Archivo Narrow', system-ui, sans-serif" };

const BIMG = 'https://biminihydrotherapy.com/images';
const HUB_UTM = 'utm_source=ninjathlete&utm_medium=affiliate&utm_campaign=hydrotherapy_hub';

const LINEUP = [
  {
    key: 'eco', tab: 'At home', icon: Home, name: 'Bimini NanoJet Eco',
    main: { src: `${BIMG}/nanojet-eco/nanojet-eco-03-tub-scale.jpg`, alt: 'NanoJet Eco beside a standard bathtub for scale' },
    details: [
      { src: `${BIMG}/nanojet-eco/nanojet-eco-08-carry-handle.jpg`, alt: 'NanoJet Eco carry handle' },
      { src: `${BIMG}/nanojet-eco/nanojet-eco-10-in-the-box.jpg`, alt: 'What comes in the NanoJet Eco box' },
    ],
    where: 'Your own bathtub.',
    facts: ['Fits most standard bathtubs', '110V outlet, no installation', '18 × 18 × 21 in, 60 lb', '2-year warranty, 30-day money-back guarantee'],
    price: '$9,650', badge: '10% off with NINJA',
    cta: { label: 'Shop NanoJet Eco', href: `https://biminihydrotherapy.com/products/nanojet-eco?ref=NINJA&checkout=1&${HUB_UTM}&utm_content=lineup%7Cnanojet-eco` },
    sub: { text: 'Comparing Eco and Pro? Read the buying guide', href: '/blogs/article/bimini-nanojet-oxygen-therapy-athletes' },
  },
  {
    key: 'pro', tab: 'Team & gym whirlpool', icon: Building2, name: 'Bimini NanoJet Pro',
    main: { src: `${BIMG}/nanojet-pro/pro-03-plunge-room.webp`, alt: 'NanoJet Pro running in a facility recovery room' },
    details: [
      { src: `${BIMG}/nanojet-pro/pro-02-spa-suite.webp`, alt: 'NanoJet Pro in a spa suite' },
      { src: `${BIMG}/nanojet-pro/pro-01-hero-studio.webp`, alt: 'Bimini NanoJet Pro unit' },
    ],
    where: 'An existing whirlpool or hot tub that seats up to five. Tub not included.',
    facts: ['Up to 5 people at once', '110V or 220V, no added plumbing', '20 lb unit', '3-year commercial warranty'],
    proof: 'Run in the training facilities at Rice University and SMU.',
    price: '$24,995', badge: '10% off with NINJA',
    cta: { label: 'Explore NanoJet Pro', href: `https://biminihydrotherapy.com/products/nanojet-pro?ref=NINJA&checkout=1&${HUB_UTM}&utm_content=lineup%7Cnanojet-pro` },
  },
  {
    key: 'tub', tab: 'Turnkey tub', icon: Bath, name: 'Bimini NanoJet Integrated Recovery Tub',
    main: { src: `${BIMG}/nanojet-all-in/all-in-04-glow-night.webp`, alt: 'Bimini Integrated Recovery Tub lit at night' },
    details: [
      { src: `${BIMG}/nanojet-all-in/all-in-02-three-quarter.webp`, alt: 'Integrated Recovery Tub three-quarter view' },
      { src: `${BIMG}/nanojet-all-in/all-in-03-side-panel.webp`, alt: 'Integrated Recovery Tub side panel' },
    ],
    where: 'Indoor or outdoor at a wellness center, spa or facility with no whirlpool yet.',
    facts: ['650 L tub, system built in', 'Heats 70°F to 104°F', 'Filtration, ozone, WiFi control', '3-year commercial warranty'],
    price: '$34,995',
    cta: { label: 'Explore the Integrated Tub', href: `https://biminihydrotherapy.com/products/nanojet-all-in?ref=NINJA&${HUB_UTM}&utm_content=lineup%7Cnanojet-all-in` },
  },
  {
    key: 'lounge', tab: 'Spa & clinic chair', icon: Armchair, name: 'Bimini O2 HydroLounge',
    main: { src: `${BIMG}/hydrolounge/hydrolounge_1.jpg`, alt: 'Bimini O2 HydroLounge chair with oxygen nanobubble foot soak' },
    details: [
      { src: `${BIMG}/hydrolounge/hydrolounge_2.jpg`, alt: 'HydroLounge foot soak basin' },
      { src: `${BIMG}/hydrolounge/hydrolounge_3.jpg`, alt: 'HydroLounge in a treatment room' },
    ],
    where: 'Spa, clinic or chiropractic room, often paired with IV or red light.',
    facts: ['Full-recline massage chair', 'Built-in 5 LPM oxygen concentrator', 'No plumbing rough-in, 55 × 20 in footprint', '30-minute sessions'],
    price: '$10,750',
    cta: { label: 'Explore HydroLounge', href: `https://biminihydrotherapy.com/products/hydrolounge?ref=NINJA&${HUB_UTM}&utm_content=lineup%7Chydrolounge` },
  },
  {
    key: 'pure', tab: 'Skincare', icon: Sparkles, name: 'Bimini Pure O2 Facial Cleanser',
    main: { src: `${BIMG}/pure-o2/pure-o2-lifestyle.jpg`, alt: 'Bimini Pure O2 Facial Cleanser in use' },
    details: [
      { src: `${BIMG}/pure-o2/pure-o2-product-1.jpg`, alt: 'Pure O2 cleanser pouches' },
      { src: `${BIMG}/pure-o2/pure-o2-facial.jpg`, alt: 'Applying Pure O2 cleanser' },
    ],
    where: 'Your daily skincare routine.',
    facts: ['30-day kit', 'Two 250 mL pouches + 60 sponges', 'Two ingredients: oxygen and distilled water', 'Free shipping'],
    price: '$46.99', badge: '10% off with NINJA ($42.29)',
    cta: { label: 'Shop Pure O2', href: `https://biminihydrotherapy.com/products/pure-o2-facial-cleanser?ref=NINJA&${HUB_UTM}&utm_content=lineup%7Cpure-o2` },
  },
];

const LOCATIONS_URL = `https://biminihydrotherapy.com/locations?${HUB_UTM}&utm_content=locations`;

const FAQS = [
  { q: 'What is oxygen nanobubble hydrotherapy?',
    a: <>Oxygen nanobubble hydrotherapy is a warm-water soak in which a device such as the Bimini NanoJet fills the water with oxygen nanobubbles. Bimini specifies bubbles averaging 0.01 microns, about 300 million per milliliter, made from 95%+ pure oxygen and saturating the water to 28 ppm. It is a wellness routine, not a medical treatment.</> },
  { q: 'How is it different from a regular bath or hot tub jets?',
    a: <>Hot tub jets push ordinary air bubbles that rise to the surface and pop within seconds. A nanobubble generator makes bubbles far too small to see individually and saturates the water with oxygen rather than air. The soak itself feels like a normal warm bath.</> },
  { q: 'How long is a session?',
    a: <>Bimini lists 30 to 60 minute sessions for the NanoJet Eco and 30 minute sessions for the O2 HydroLounge. Water temperature is whatever is comfortable for a long soak; the Integrated Recovery Tub heats from 70°F to 104°F. Follow Bimini's current operating instructions for your model.</> },
  { q: 'Can I use my own bathtub?',
    a: <>Yes, with the NanoJet Eco. It is built for most standard bathtubs, plugs into a 110V outlet and needs no permanent installation or plumbing. Share your tub dimensions with Bimini to confirm fit before ordering.</> },
  { q: 'Which system fits a gym, team facility or spa?',
    a: <>The NanoJet Pro connects to an existing whirlpool that seats up to five people (tub not included). The NanoJet Integrated Recovery Tub is a turnkey 650 L tub with the system built in. The O2 HydroLounge is a massage chair with a nanobubble foot soak and a built-in oxygen concentrator, suited to spas and clinics.</> },
  { q: 'Is it the same as hyperbaric oxygen therapy?',
    a: <>No. <Link to="/blogs/article/hbot-vs-nanobubble-hydrotherapy" className="underline font-semibold" style={{ textDecorationColor: LIME }}>Hyperbaric oxygen therapy</Link> means breathing oxygen inside a pressurized chamber. Nanobubble hydrotherapy is a soak in oxygenated water at normal pressure. Research on hyperbaric therapy does not transfer to bath-based devices.</> },
  { q: 'Is there research proving it speeds recovery?',
    a: <>Not yet. Bimini's bubble size, density and saturation figures are manufacturer specifications, and athlete feedback is user-reported. Peer-reviewed, controlled human trials showing faster recovery from oxygen nanobubble baths have not been published. Bimini describes its systems as wellness devices, not medical devices.</> },
  { q: 'Where can I try one before buying?',
    a: <>Bimini keeps a list of facilities that offer sessions on its <a href={LOCATIONS_URL} target="_blank" rel="sponsored noopener" className="underline font-semibold" style={{ textDecorationColor: LIME }}>locations page</a>. Collegiate athletic programs including Rice University and SMU run the NanoJet Pro in their training facilities.</> },
  { q: 'How do I get the NINJA discount?',
    a: <>Code NINJA takes 10% off the NanoJet Eco, NanoJet Pro and Pure O2 Facial Cleanser. The links on this page carry the code to Bimini, which confirms the final price at checkout. The code does not apply to shipping. NinjAthlete may earn a commission on purchases.</> },
  { q: 'How long does shipping take?',
    a: <>NanoJet systems dispatch in about four weeks, then take 3 to 4 business days in transit. Shipping is a flat $150 per order to the contiguous US and DC, $250 to Hawaii and $850 to supported international countries. Pure O2 ships free in the US. Returns fall under Bimini's 30-day policy; return shipping is paid by the customer.</> },
];

const SPECS = [
  { v: '0.01 µm', l: 'average bubble size' },
  { v: '~300M', l: 'O₂ bubbles per mL' },
  { v: '95%+', l: 'oxygen purity' },
  { v: '28 ppm', l: 'water saturation' },
];

const STEPS = [
  { n: '01', t: 'Place the hose in the tub', img: `${BIMG}/nanojet-eco/nanojet-eco-04-hose-in-tub.jpg`, alt: 'NanoJet Eco hose placed in a filled bathtub',
    text: 'Fill your tub with warm water and set the NanoJet hose in place. The Eco plugs into a standard 110V outlet. No plumbing.' },
  { n: '02', t: 'Start the NanoJet', img: `${BIMG}/nanojet-eco/nanojet-eco-06-control-panel.jpg`, alt: 'Bimini NanoJet Eco control panel',
    text: 'The unit draws oxygen through its concentrator and pushes it into the water as nanobubbles.' },
  { n: '03', t: 'Soak for 30 to 60 minutes', img: `${BIMG}/nanojet-eco/nanojet-eco-11-soak.jpg`, alt: 'Person soaking in a bath running the Bimini NanoJet Eco',
    text: "Water temperature is whatever is comfortable for a long soak. Follow Bimini's operating instructions for your model." },
];

const DEEPER = [
  { t: 'Bimini NanoJet Eco vs. Pro buying guide', d: 'Which system fits your space, budget and setup.', href: '/blogs/article/bimini-nanojet-oxygen-therapy-athletes' },
  { t: 'HBOT vs. nanobubble hydrotherapy', d: 'Pressurized chamber or warm soak: what the evidence covers.', href: '/blogs/article/hbot-vs-nanobubble-hydrotherapy' },
  { t: 'Gym recovery amenities ranked by ROI', d: 'What facility owners should install first.', href: '/blogs/article/recovery-amenities-gym-economics' },
  { t: 'How cold should a cold plunge be?', d: 'Dial in cold-water immersion temperature.', href: '/blogs/article/how-cold-should-a-cold-plunge-be' },
  { t: 'Oxygen and your brain', d: 'What oxygen does for focus and recovery overnight.', href: '/blogs/article/oxygen-and-your-brain' },
  { t: 'All recovery guides', d: 'The full recovery library.', href: '/recovery' },
];

export default function HydrotherapyPage() {
  const [tab, setTab] = useState('eco');
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    const kw = document.querySelector('meta[name="keywords"]');
    if (kw) kw.remove();
    let script = document.getElementById('hydrotherapy-hub-schema');
    if (!script) {
      script = document.createElement('script');
      script.id = 'hydrotherapy-hub-schema';
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(HUB_SCHEMA);
    return () => { script.remove(); };
  }, []);

  const goLineup = (key) => {
    setTab(key);
    document.getElementById('lineup')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const active = LINEUP.find((p) => p.key === tab);

  return (
    <main className="min-h-screen" style={{ background: '#FFFFFF', color: INK, ...ARCHIVO }} data-testid="hydrotherapy-page">
      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="max-w-6xl mx-auto px-5 pt-6 text-sm" style={{ color: MUT }} data-testid="hydro-breadcrumb">
        <Link to="/" className="hover:underline">Home</Link>
        <ChevronRight className="inline w-3.5 h-3.5 mx-1 -mt-0.5" />
        <Link to="/recovery" className="hover:underline">Recovery</Link>
        <ChevronRight className="inline w-3.5 h-3.5 mx-1 -mt-0.5" />
        <span style={{ color: INK }} className="font-semibold">Oxygen Nanobubble Hydrotherapy</span>
      </nav>

      {/* 1. HERO */}
      <section data-placement="hero" className="max-w-6xl mx-auto px-5 pt-8 pb-14 grid grid-cols-1 md:grid-cols-2 gap-10 items-center" data-testid="hydro-hero">
        <div>
          <span className="inline-block text-[12px] font-extrabold tracking-[0.2em] px-3 py-1.5 rounded-sm mb-5" style={{ background: LIME, color: '#3A4A10' }}>
            NINJATHLETE × BIMINI
          </span>
          <h1 className="text-5xl md:text-6xl leading-[0.98] mb-5" style={ANTON} data-testid="hydro-h1">
            Oxygen Nanobubble Hydrotherapy
          </h1>
          <p id="hub-answer" className="text-lg leading-relaxed mb-7" style={{ color: '#2A2A26' }}>
            Oxygen nanobubble hydrotherapy is a warm-water soak in water saturated with microscopic oxygen bubbles. A device like the Bimini NanoJet makes bubbles averaging 0.01 microns from 95%+ pure oxygen. You sit in it for 30 to 60 minutes, at home in your own tub or at a facility. It is a wellness routine, not a medical treatment.
          </p>
          <div className="flex flex-wrap gap-3 mb-5">
            <button onClick={() => goLineup('eco')} className="px-6 py-3.5 font-extrabold text-sm uppercase tracking-wide rounded-md transition-transform hover:-translate-y-0.5" style={{ background: LIME, color: INK }} data-testid="hydro-hero-home-btn">
              See home systems
            </button>
            <button onClick={() => goLineup('pro')} className="px-6 py-3.5 font-extrabold text-sm uppercase tracking-wide rounded-md border-2 transition-transform hover:-translate-y-0.5" style={{ borderColor: INK, color: INK, background: '#fff' }} data-testid="hydro-hero-facility-btn">
              See facility systems
            </button>
          </div>
          <p className="text-[13px]" style={{ color: MUT }}>
            Code NINJA: 10% off NanoJet Eco, Pro &amp; Pure O2. NinjAthlete may earn a commission.
          </p>
        </div>
        <img
          src="https://biminihydrotherapy.com/images/nanojet-eco/nanojet-eco-02-spa-bathroom.webp"
          alt="Bimini NanoJet Eco set up beside a bathtub in a home spa bathroom"
          width="1774" height="887" fetchpriority="high"
          className="w-full h-auto rounded-2xl border" style={{ borderColor: LINE }}
          data-testid="hydro-hero-image"
        />
      </section>

      {/* 2. SPEC STRIP */}
      <section className="max-w-6xl mx-auto px-5 pb-14" data-testid="hydro-spec-strip">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {SPECS.map((s) => (
            <div key={s.l} className="rounded-xl px-5 py-6 text-center border" style={{ background: SOFT, borderColor: LINE }}>
              <div className="text-3xl mb-1" style={{ ...ANTON, textTransform: 'none' }}>{s.v}</div>
              <div className="text-[13px]" style={{ color: MUT }}>{s.l}</div>
            </div>
          ))}
        </div>
        <p className="text-xs mt-3 text-center" style={{ color: MUT }}>Manufacturer specifications from Bimini. Not independently verified.</p>
      </section>

      {/* 3. SESSION */}
      <section id="session" data-placement="session" className="max-w-6xl mx-auto px-5 pb-16" data-testid="hydro-session">
        <h2 className="text-4xl md:text-5xl mb-8 pt-4 border-t-4" style={{ ...ANTON, borderColor: INK }}>What a session looks like</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {STEPS.map((st) => (
            <div key={st.n}>
              <img src={st.img} alt={st.alt} loading="lazy" width="1200" height="800" className="w-full aspect-[4/3] object-cover rounded-xl border" style={{ borderColor: LINE }} />
              <div className="mt-4 flex items-baseline gap-3">
                <span className="text-2xl" style={{ ...ANTON, color: '#9AA38B' }}>{st.n}</span>
                <h3 className="font-extrabold text-lg">{st.t}</h3>
              </div>
              <p className="text-[15px] mt-1.5 leading-relaxed" style={{ color: '#3A3A36' }}>{st.text}</p>
            </div>
          ))}
        </div>
        <figure className="mt-10 max-w-3xl mx-auto">
          <video controls preload="none" poster="https://biminihydrotherapy.com/videos/bimini-home-unit-poster.jpg" className="w-full rounded-xl border" style={{ borderColor: LINE }} data-testid="hydro-home-video">
            <source src="https://biminihydrotherapy.com/videos/bimini-home-unit.mp4" type="video/mp4" />
          </video>
          <figcaption className="text-[13px] mt-2 text-center" style={{ color: MUT }}>Bimini co-founder Jason Applewhite walks through a home setup (0:35)</figcaption>
        </figure>
      </section>

      {/* 4. LINEUP */}
      <section id="lineup" data-placement="lineup" data-bimini-list="hydrotherapy_hub_lineup" className="max-w-6xl mx-auto px-5 pb-16 scroll-mt-24" data-testid="hydro-lineup">
        <h2 className="text-4xl md:text-5xl mb-3 pt-4 border-t-4" style={{ ...ANTON, borderColor: INK }}>Find your format</h2>
        <p className="text-lg mb-7" style={{ color: '#3A3A36' }}>Bimini makes the same nanobubble system in five formats. Pick by where it will live.</p>
        <div className="flex flex-wrap gap-2 mb-8" role="tablist" aria-label="Bimini formats">
          {LINEUP.map((p) => {
            const Icon = p.icon;
            const on = tab === p.key;
            return (
              <button
                key={p.key} role="tab" aria-selected={on} onClick={() => setTab(p.key)}
                className="inline-flex items-center gap-2 px-4 py-2.5 text-[13px] font-extrabold uppercase tracking-wide rounded-full border-2 transition-colors"
                style={on ? { background: INK, color: '#fff', borderColor: INK } : { background: '#fff', color: INK, borderColor: LINE }}
                data-testid={`hydro-tab-${p.key}`}
              >
                <Icon className="w-4 h-4" style={{ color: on ? LIME : INK }} /> {p.tab}
              </button>
            );
          })}
        </div>
        {active && (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8" data-testid={`hydro-panel-${active.key}`}>
            <div className="lg:col-span-3">
              <img src={active.main.src} alt={active.main.alt} loading="lazy" width="1200" height="800" className="w-full aspect-[4/3] object-cover rounded-xl border" style={{ borderColor: LINE }} />
              <div className="grid grid-cols-2 gap-4 mt-4">
                {active.details.map((d) => (
                  <img key={d.src} src={d.src} alt={d.alt} loading="lazy" width="600" height="420" className="w-full aspect-[10/7] object-cover rounded-lg border" style={{ borderColor: LINE }} />
                ))}
              </div>
            </div>
            <div className="lg:col-span-2">
              <div className="rounded-2xl border p-7 sticky top-24" style={{ borderColor: LINE, background: SOFT }}>
                <h3 className="text-2xl mb-2" style={ANTON}>{active.name}</h3>
                <p className="text-sm font-bold mb-4" style={{ color: '#3A3A36' }}>Where it goes: <span className="font-normal">{active.where}</span></p>
                <ul className="space-y-2 mb-5">
                  {active.facts.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-[15px]">
                      <Check className="w-4 h-4 mt-1 flex-shrink-0" style={{ color: '#5A7A00' }} /> {f}
                    </li>
                  ))}
                </ul>
                {active.proof && <p className="text-[13px] italic mb-4 pb-4 border-b" style={{ color: MUT, borderColor: LINE }}>{active.proof}</p>}
                <div className="flex items-center gap-3 mb-5">
                  <span className="text-3xl" style={ANTON} data-testid={`hydro-price-${active.key}`}>{active.price}</span>
                  {active.badge && <span className="text-[11px] font-extrabold px-2.5 py-1 rounded-full" style={{ background: LIME, color: '#3A4A10' }}>{active.badge}</span>}
                </div>
                <a href={active.cta.href} target="_blank" rel="sponsored noopener" className="block text-center px-6 py-4 font-extrabold text-sm uppercase tracking-wide rounded-md transition-transform hover:-translate-y-0.5" style={{ background: LIME, color: INK }} data-testid={`hydro-cta-${active.key}`}>
                  {active.cta.label}
                </a>
                {active.sub && (
                  <Link to={active.sub.href} className="block text-center text-sm font-semibold mt-3 underline" style={{ textDecorationColor: LIME }} data-testid="hydro-eco-guide-link">
                    {active.sub.text}
                  </Link>
                )}
              </div>
            </div>
          </div>
        )}
        <p className="text-xs mt-8" style={{ color: MUT }}>
          Prices from Bimini as of October 2026, before shipping and tax. NanoJet systems ship for a flat $150 per order (contiguous US and DC), dispatching in about 4 weeks plus 3–4 business days transit. Pure O2 ships free.
        </p>
      </section>

      {/* 5. WHERE YOU'LL FIND IT */}
      <section data-placement="locations" className="max-w-6xl mx-auto px-5 pb-16" data-testid="hydro-locations">
        <h2 className="text-4xl md:text-5xl mb-8 pt-4 border-t-4" style={{ ...ANTON, borderColor: INK }}>Where you'll find it</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
          <figure>
            <video controls preload="none" poster="https://biminihydrotherapy.com/videos/bimini-samples-poster.jpg" className="w-full rounded-xl border" style={{ borderColor: LINE }} data-testid="hydro-facilities-video">
              <source src="https://biminihydrotherapy.com/videos/bimini-samples.mp4" type="video/mp4" />
            </video>
            <figcaption className="text-[13px] mt-2" style={{ color: MUT }}>NanoJet systems running in real training facilities</figcaption>
          </figure>
          <div>
            <ul className="space-y-4 mb-7">
              {[
                { icon: Home, t: 'Home bathrooms', d: 'NanoJet Eco' },
                { icon: Building2, t: 'Collegiate training rooms', d: 'Rice University and SMU run the NanoJet Pro' },
                { icon: Bath, t: 'Recovery studios and spas', d: 'NanoJet Pro, Integrated Tub, HydroLounge' },
                { icon: Armchair, t: 'Clinics', d: 'O2 HydroLounge' },
              ].map((r) => {
                const Icon = r.icon;
                return (
                  <li key={r.t} className="flex items-start gap-4 pb-4 border-b" style={{ borderColor: LINE }}>
                    <span className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: SOFT }}>
                      <Icon className="w-5 h-5" />
                    </span>
                    <div>
                      <p className="font-extrabold">{r.t}</p>
                      <p className="text-[14px]" style={{ color: MUT }}>{r.d}</p>
                    </div>
                  </li>
                );
              })}
            </ul>
            <div className="flex flex-wrap gap-3">
              <a href={LOCATIONS_URL} target="_blank" rel="sponsored noopener" className="inline-flex items-center gap-2 px-5 py-3 font-extrabold text-sm uppercase tracking-wide rounded-md" style={{ background: LIME, color: INK }} data-testid="hydro-bimini-locations-btn">
                <MapPin className="w-4 h-4" /> Find a Bimini location
              </a>
              <Link to="/locator" className="inline-flex items-center gap-2 px-5 py-3 font-extrabold text-sm uppercase tracking-wide rounded-md border-2" style={{ borderColor: INK, color: INK }} data-testid="hydro-recovery-locator-btn">
                Browse our Recovery Locator <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* 6. WHAT'S ESTABLISHED */}
      <section className="py-16" style={{ background: BLUE, color: '#fff' }} data-testid="hydro-evidence">
        <div className="max-w-6xl mx-auto px-5">
          <h2 className="text-4xl md:text-5xl mb-9" style={ANTON}>What we know, and what we don't</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-7">
            {[
              { t: 'Manufacturer specs', d: 'Bubble size, density, oxygen purity, saturation, session length. These describe the hardware. Bimini publishes them; we have not tested them.' },
              { t: 'User-reported', d: 'Athletes and facilities describe how sessions feel and how often they use them. Useful for deciding what to try. Not a measurement of recovery.' },
              { t: "Not established yet", d: "Peer-reviewed, controlled human trials showing faster recovery from oxygen nanobubble baths. If that evidence lands, we'll update this page." },
            ].map((c) => (
              <div key={c.t} className="rounded-xl p-6" style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.14)' }}>
                <h3 className="text-xl mb-3" style={ANTON}>{c.t}</h3>
                <p className="text-[15px] leading-relaxed" style={{ color: '#CBD8EA' }}>{c.d}</p>
              </div>
            ))}
          </div>
          <p className="mt-9 text-sm font-bold">
            Tested. Not Hyped.{' '}
            <Link to="/blogs/article/hbot-vs-nanobubble-hydrotherapy" className="underline font-semibold" style={{ textDecorationColor: LIME, color: '#fff' }} data-testid="hydro-hbot-link">
              How this compares with hyperbaric oxygen
            </Link>
          </p>
        </div>
      </section>

      {/* 7. GO DEEPER */}
      <section className="max-w-6xl mx-auto px-5 py-16" data-testid="hydro-go-deeper">
        <h2 className="text-4xl md:text-5xl mb-8" style={ANTON}>Go deeper</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {DEEPER.map((c) => (
            <Link key={c.href} to={c.href} className="group rounded-xl border p-5 flex items-start justify-between gap-3 transition-all hover:-translate-y-0.5" style={{ borderColor: LINE, background: '#fff' }} data-testid={`hydro-deeper-${c.href.split('/').pop()}`}>
              <div>
                <p className="font-extrabold leading-snug mb-1">{c.t}</p>
                <p className="text-[13px]" style={{ color: MUT }}>{c.d}</p>
              </div>
              <ArrowRight className="w-5 h-5 flex-shrink-0 mt-0.5 transition-transform group-hover:translate-x-1" style={{ color: '#5A7A00' }} />
            </Link>
          ))}
        </div>
      </section>

      {/* 8. FAQ */}
      <section id="faq" data-placement="faq" className="max-w-4xl mx-auto px-5 pb-16" data-testid="hydro-faq">
        <h2 className="text-4xl md:text-5xl mb-8 pt-4 border-t-4" style={{ ...ANTON, borderColor: INK }}>Oxygen nanobubble hydrotherapy FAQ</h2>
        <div className="rounded-2xl border divide-y" style={{ borderColor: LINE }}>
          {FAQS.map((f, i) => {
            const open = openFaq === i;
            return (
              <div key={f.q} style={{ borderColor: LINE }}>
                <button
                  onClick={() => setOpenFaq(open ? -1 : i)}
                  aria-expanded={open}
                  className="w-full flex items-center justify-between gap-4 text-left px-6 py-5 font-extrabold text-[17px]"
                  data-testid={`hydro-faq-q-${i}`}
                >
                  {f.q}
                  <ChevronDown className="w-5 h-5 flex-shrink-0 transition-transform" style={{ transform: open ? 'rotate(180deg)' : 'none', color: '#5A7A00' }} />
                </button>
                <div className="px-6 pb-5 text-[15px] leading-relaxed" style={{ color: '#3A3A36', display: open ? 'block' : 'none' }} data-testid={`hydro-faq-a-${i}`}>
                  {f.a}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* 9. CLOSING */}
      <section data-placement="closing" className="py-16" style={{ background: SOFT }} data-testid="hydro-closing">
        <div className="max-w-4xl mx-auto px-5 text-center">
          <h2 className="text-4xl md:text-5xl mb-7" style={ANTON}>Start with the setting, not the hype.</h2>
          <div className="flex flex-wrap justify-center gap-3 mb-8">
            <a href={`https://biminihydrotherapy.com/products/nanojet-eco?ref=NINJA&checkout=1&${HUB_UTM}&utm_content=closing%7Cnanojet-eco`} target="_blank" rel="sponsored noopener" className="px-7 py-4 font-extrabold text-sm uppercase tracking-wide rounded-md" style={{ background: LIME, color: INK }} data-testid="hydro-closing-eco">
              Shop NanoJet Eco
            </a>
            <a href={`https://biminihydrotherapy.com/products/nanojet-pro?ref=NINJA&checkout=1&${HUB_UTM}&utm_content=closing%7Cnanojet-pro`} target="_blank" rel="sponsored noopener" className="px-7 py-4 font-extrabold text-sm uppercase tracking-wide rounded-md border-2" style={{ borderColor: INK, color: INK, background: '#fff' }} data-testid="hydro-closing-pro">
              Explore NanoJet Pro
            </a>
          </div>
          <p className="text-[13px] max-w-2xl mx-auto mb-2" style={{ color: MUT }}>
            Daniel Rivas, NinjAthlete's founder, is Bimini's marketing director. NinjAthlete may earn a commission from purchases through these links. Bimini is a wellness device, not a medical device.
          </p>
          <p className="text-[13px]" style={{ color: MUT }}>Updated October 21, 2026 · NinjAthlete Team</p>
        </div>
      </section>

      {/* Existing newsletter + locator blocks (unchanged) */}
      <section className="py-16" style={{ background: '#0A0A0A' }}>
        <div className="max-w-6xl mx-auto px-5 space-y-12">
          <LeadCaptureForm
            source="hydrotherapy_hub"
            title="Get Recovery Tips & Exclusive Deals"
            description="Science-backed recovery protocols, Bimini updates, and exclusive NinjAthlete discounts."
            buttonText="Join the Recovery Revolution"
            interests={['hydrotherapy', 'recovery', 'biohacking']}
          />
          <LocatorFeeder
            heading="Find hydrotherapy & recovery facilities near you"
            blurb="Cold plunge, contrast suites and float studios — 540+ vetted facilities across 25 metros in our free Recovery Locator."
            therapies={[
              { slug: 'cold-plunge', name: 'Cold Plunge' },
              { slug: 'contrast-therapy', name: 'Contrast Therapy' },
              { slug: 'float-therapy', name: 'Float Therapy' },
            ]}
            testId="hydrotherapy-locator-feeder"
          />
        </div>
      </section>

      <PageSEO
        title="Oxygen Nanobubble Hydrotherapy: Bimini Guide | NinjAthlete"
        description="How oxygen nanobubble hydrotherapy works, what a warm-water session involves, and every Bimini system for home, gym or spa, with real photos and specs."
        path="/hydrotherapy"
        image="https://ninjathlete.com/images/hydrotherapy/nanojet-eco-02-spa-bathroom.webp"
      />
    </main>
  );
}
